import { ArrowLeft, Upload } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'

interface UploadPaymentDetailsScreenProps {
  onNext: () => void
  onBack: () => void
}

const UploadPaymentDetailsScreen = ({
  onNext,
  onBack,
}: UploadPaymentDetailsScreenProps) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setUploadedFile(file)
    }
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const file = event.dataTransfer.files[0]
    if (file) {
      setUploadedFile(file)
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  return (
    <div className="h-[600px] flex flex-col">
      <div className="p-4 space-y-2 flex-1 flex flex-col">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-lg font-bold text-gray-900">
            Upload Payment Details
          </h1>
          <p className="text-sm text-gray-600">
            Upload your document and we'll handle the rest
          </p>
        </div>

        {/* Content - Main area */}
        <div className="flex-1 space-y-2">
          {/* Steps */}
          <div className="bg-gray-50 rounded-xl p-2 space-y-1">
            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                1
              </div>
              <div>
                <p className="text-gray-900 font-medium text-sm">
                  Upload a bill, invoice, or screenshot with payment details.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                2
              </div>
              <div>
                <p className="text-gray-900 font-medium text-sm">
                  We autofill the recipient's details.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <div className="w-5 h-5 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0">
                3
              </div>
              <div>
                <p className="text-gray-900 font-medium text-sm">
                  Review the information then send.
                </p>
              </div>
            </div>
          </div>

          {/* Upload Area */}
          <div
            className="border-2 border-dashed border-gray-300 rounded-xl p-3 text-center space-y-2 hover:border-orange-300 transition-colors flex-1 flex flex-col justify-center"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mx-auto">
              <Upload className="w-5 h-5 text-orange-500" />
            </div>

            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-gray-900">
                Upload your document
              </h3>
              <p className="text-sm text-gray-600">
                Drag and drop your file here, or click to browse
              </p>
              <p className="text-xs text-gray-500">
                Supported formats: PDF, PNG, JPG, JPEG (Max 10MB)
              </p>
            </div>

            <div className="pt-2">
              <label htmlFor="file-upload">
                <Button
                  type="button"
                  className="bg-black text-white hover:bg-gray-800 px-3 py-2 rounded-lg font-medium text-sm h-8"
                  onClick={() =>
                    document.getElementById('file-upload')?.click()
                  }
                >
                  Choose File
                </Button>
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>

            {uploadedFile && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-xs font-medium">
                  File uploaded: {uploadedFile.name}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="pt-1 border-t border-gray-100">
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onBack}
              className="flex-1 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border border-gray-300 hover:bg-yellow-50 hover:border-yellow-200 transition-colors h-10 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </Button>

            <Button
              onClick={onNext}
              disabled={!uploadedFile}
              className="flex-1 bg-black text-white hover:bg-gray-800 font-medium transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed h-10 text-sm"
            >
              Continue
            </Button>
          </div>
        </div>
      </div>

      {/* Powered by Paytrie - Swapped order */}
      <div className="text-center p-2 border-t border-gray-100">
        <p className="text-xs text-gray-500 mb-1">Powered by Paytrie</p>
        <img
          src="/paytrie-logo.png"
          alt="Paytrie"
          className="h-6 w-auto mx-auto"
        />
      </div>
    </div>
  )
}

export default UploadPaymentDetailsScreen
